import {
  HttpClient,
  HttpErrorResponse,
  HttpParams,
} from '@angular/common/http';
import { Observable } from 'rxjs';
import { tap } from 'rxjs/operators';
import { ToastMessageService } from '@core/services/toast-message.service';
import { inject, Injectable } from '@angular/core';
import { ApiResponse } from '@core/models/general.model';
import { environment } from '@environments/environment';
import { ErrorService } from '@core/services/error.service';

export type DeepPartial<T> = {
  [P in keyof T]?: T[P] extends object ? DeepPartial<T[P]> : T[P];
};

@Injectable({
  providedIn: 'root',
})
/**
 * Clase base para servicios CRUD. Subclases pasan su endpoint en el constructor.
 * `entityName` se usa en los mensajes de toast; `showSuccess` permite silenciar el toast en flujos
 * donde el componente maneja el feedback (p.ej. login, guardado con lógica adicional).
 */
export abstract class BaseService<T> {
  toast = inject(ToastMessageService);
  errorService = inject(ErrorService);

  private environment = environment;

  constructor(
    protected http: HttpClient,
    protected endpoint: string,
  ) {}

  /** Reemplaza el endpoint dinámicamente cuando la URL completa viene del caller. */
  setApiUrl(fullUrl: string): void {
    this.endpoint = fullUrl;
  }

  getAll(entityName: string): Observable<T[]> {
    return this.http.get<T[]>(`${this.environment.apiUrl}/${this.endpoint}`);
  }

  getPaginated(
    params: HttpParams,
    endpoint: string,
    entityName: string,
  ): Observable<ApiResponse<T>> {
    return this.http.get<ApiResponse<T>>(
      `${this.environment.apiUrl}/${endpoint}`,
      { params },
    );
  }

  getById(id: number, entityName: string): Observable<T> {
    return this.http.get<T>(
      `${this.environment.apiUrl}/${this.endpoint}/${id}`,
    );
  }

  /** GET /:endpoint/:id — retorna array (relaciones 1-N bajo el mismo ID). */
  getAllById(id: number, entityName: string): Observable<T[]> {
    return this.http.get<T[]>(
      `${this.environment.apiUrl}/${this.endpoint}/${id}`,
    );
  }

  create(
    item: T,
    entityName: string,
    showSuccess: boolean = true,
  ): Observable<T> {
    return this.http
      .post<T>(`${this.environment.apiUrl}/${this.endpoint}`, item)
      .pipe(
        tap(() => {
          if (showSuccess) {
            this.toast.showSuccess(
              'Éxito',
              `${entityName} creado exitosamente`,
            );
          }
        }),
      );
  }

  update(
    item: Partial<T>,
    entityName: string,
    showSuccess: boolean = true,
  ): Observable<T> {
    return this.http
      .put<T>(`${this.environment.apiUrl}/${this.endpoint}`, item)
      .pipe(
        tap(() => {
          if (showSuccess) {
            this.toast.showSuccess(
              'Éxito',
              `${entityName} actualizado exitosamente`,
            );
          }
        }),
      );
  }

  delete(
    item: any,
    entityName: string,
    showSuccess: boolean = true,
  ): Observable<void> {
    return this.http
      .delete<void>(`${this.environment.apiUrl}/${this.endpoint}`, {
        body: item,
      })
      .pipe(
        tap(() => {
          if (showSuccess) {
            this.toast.showSuccess(
              'Éxito',
              `${entityName} eliminado exitosamente`,
            );
          }
        }),
      );
  }

  handleError(error: HttpErrorResponse, entityName: string): Observable<any> {
    return this.errorService.handleError(error, entityName);
  }
}
